import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { motion } from "framer-motion"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import theme from "lib/material-ui/theme"
import ArrowIcon from "components/atoms/_icons/ArrowIcon"
import BottomSheet from "components/atoms/BottomSheet"

type CurrencySectionProps = {
  selectedCurrency: string;
  defaultCurrency: string;
  currencies: string[];
  onChange: (currency: string) => void;
}

export default function CurrencySection(props: CurrencySectionProps) {
  const { selectedCurrency, defaultCurrency, currencies, onChange } = props

  const [showCurrenciesBottomSheet, setShowCurrenciesBottomSheet] = useState<boolean>(false)

  const { t } = useTranslation()

  const firstRender = useRef(true)

  useEffect(() => {
    firstRender.current = false
  }, [])

  function handleOnSelectedCurrencyClicked() {
    setShowCurrenciesBottomSheet(true)
  }

  function handleOnBottomSheetClose() {
    setShowCurrenciesBottomSheet(false)
  }

  function handleOnCurrencyOptionClicked(currency: string) {
    handleOnBottomSheetClose()
    onChange(currency)
  }

  return (
    <>
      <Container>
        <CurrencyWrapper
          style={{
            backgroundColor: showCurrenciesBottomSheet ? theme.palette.gray[5] : "transparent"
          }}
          onClick={handleOnSelectedCurrencyClicked}
        >
          {t(`currency.${selectedCurrency || defaultCurrency}`)}
          <motion.div
            animate={{ rotate: showCurrenciesBottomSheet ? 90 : -90 }}
            transition={{
              duration: firstRender.current ? 0 : 0.3,
              ease: "easeInOut"
            }}
            style={{ display: "flex", alignItems: "center", transform: "rotate(270deg)" }}
          >
            <ArrowIcon/>
          </motion.div>
        </CurrencyWrapper>
      </Container>

      <BottomSheet
        id="currencies"
        show={showCurrenciesBottomSheet}
        openBottomSheet={() => {
        }}
        closeBottomSheet={handleOnBottomSheetClose}
      >
        <Stack direction="row" flexWrap="wrap" gap="8px">
          {currencies.map((currency) => (
            <CurrencyOption
              key={currency}
              onClick={() => handleOnCurrencyOptionClicked(currency)}
            >
              {t(`currency.${currency}`)}
            </CurrencyOption>
          ))}
        </Stack>
      </BottomSheet>
    </>
  )
}

const Container = styled("div")(({ theme }) => ({
  width: "80px",
  height: "34px",
  borderLeft: `1px solid ${theme.palette.gray[7]}`,
  padding: "0 4px"
}))

const CurrencyWrapper = styled(Stack)(({ theme }) => ({
  width: "100%",
  height: "100%",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
  borderRadius: "16px",
  fontSize: "14px",
  color: theme.palette.gray[14],
  paddingLeft: "2px",

  "& svg": {
    width: "20px !important",
    height: "20px !important",
    stroke: `${theme.palette.gray[12]} !important`
  }
}))

const CurrencyOption = styled(Stack)(({ theme }) => ({
  width: "calc(50% - 4px)",
  height: "54px",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  backgroundColor: theme.palette.gray[5],
  borderRadius: "128px",
  fontSize: "14px",
  color: theme.palette.gray[15],
  cursor: "pointer"
}))
